import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "./root.store";
import type { AiDashboardSummary } from "@/types/reporting-agent";

/**
 * Holds the reports the Reporting Teammate generated — static snapshots stored
 * under `reporting-agent/dashboards/` — that the Reports index lists beside the
 * built ones (NG-1588) and the viewer's breadcrumb resolves titles from.
 * Additive and best-effort: it's only loaded when the reporting-agent flag is on,
 * and a load failure degrades to "no generated reports" (the built list still
 * renders) rather than breaking the page.
 */
export class ReportingAgentDashboardsStore {
  items: AiDashboardSummary[] = [];
  loading = false;
  loaded = false;
  /** The workspace `items` belong to — set only when an answer lands, never when
   *  a fetch starts, so a page gating on it can never show a stale list while the
   *  new workspace's answer is still in flight. */
  loadedSlug: string | null = null;
  /** Set when something happened that the loaded list cannot know about — the
   *  chat just built a report (NG-1948) — so the next `ensure` refetches. The
   *  old rows stay on screen until the fresh answer lands; nothing blanks. */
  stale = false;
  /** Counts invalidations, so a fetch can tell whether one happened while it was
   *  in flight. Without it an answer that left before the report existed would
   *  land afterwards and clear a staleness it predates, and `ensure` would then
   *  serve that pre-report list for the rest of the session. */
  private invalidations = 0;
  /** The workspace a fetch is in flight for; a stale answer is one whose slug
   *  no longer matches. */
  private pendingSlug: string | null = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /**
   * Load the list for `slug` unless it is already loaded or loading for that
   * workspace. Two callers may ask at once — the Reports route loader (so the
   * request fans out in parallel with the reports fetch) and the page's own
   * effect (for the render where the flag resolves later than the loader) — and
   * they must share one request, not race two.
   */
  async ensure(slug: string): Promise<void> {
    if (this.pendingSlug === slug) return;
    if (this.loaded && this.loadedSlug === slug && !this.stale) return;
    await this.fetch(slug);
  }

  /**
   * The list on hand is out of date — a report was just generated in the chat
   * (NG-1948) — so the next `ensure` must refetch. Rows are kept, not dropped:
   * a Reports page already showing them keeps them until the answer lands.
   */
  invalidate(): void {
    this.invalidations += 1;
    this.stale = true;
  }

  async fetch(slug: string): Promise<void> {
    this.loading = true;
    this.pendingSlug = slug;
    // Read before the await: the answer below is only current as of this moment.
    const invalidationsAtStart = this.invalidations;
    try {
      const items = await this.rootStore.reportingAgentService.listDashboards(slug);
      runInAction(() => {
        // A newer workspace's fetch has superseded this one: its answer is the
        // one that lands, not this stale one.
        if (this.pendingSlug !== slug) return;
        this.items = items;
        this.loaded = true;
        this.loadedSlug = slug;
        // A report built while this request was in flight is not in the answer
        // it brought back, so the staleness outlives it and the next `ensure`
        // asks again. Clearing it here regardless is how a race becomes a list
        // that is wrong until the page is reloaded.
        if (this.invalidations === invalidationsAtStart) this.stale = false;
      });
    } catch (error) {
      // Optional surface — don't break the Reports page if this fails. Nothing
      // is marked loaded, so the next ensure() tries again rather than
      // trusting a miss.
      console.warn("[reporting-agent] failed to load generated reports", error);
    } finally {
      runInAction(() => {
        if (this.pendingSlug === slug) {
          this.pendingSlug = null;
          this.loading = false;
        }
      });
    }
  }

  reset(): void {
    this.items = [];
    this.loading = false;
    this.loaded = false;
    this.loadedSlug = null;
    this.stale = false;
    this.invalidations = 0;
    this.pendingSlug = null;
  }
}
